import { ComponentType } from "react";
import styled, { css, keyframes } from "styled-components";
import {
  elevation01,
  maxSmall,
  minMedium,
  shade0,
  shade700,
  shade800,
  shade900,
  shade950,
} from "../../../styles/tokens";
import { IconButton } from "../../icon-button/IconButton.Parts";
import { ToastContext } from "../Toast.Parts";
import { AttentionIcon } from "../icons/attention-icon/AttentionIcon";
import { CommentIcon } from "../icons/comment-icon/CommentIcon";
import { CopyIcon } from "../icons/copy-icon/CopyIcon";
import { DeleteIcon } from "../icons/delete-icon/DeleteIcon";
import { DownloadIcon } from "../icons/download-icon/DownloadIcon";
import { DuplicateIcon } from "../icons/duplicate-icon/DuplicateIcon";
import { ErrorIcon } from "../icons/error-icon/ErrorIcon";
import { InfoIcon } from "../icons/info-icon/InfoIcon";
import { LicenseIcon } from "../icons/license-icon/LicenseIcon";
import { LikeIcon } from "../icons/like-icon/LikeIcon";
import { MoveIcon } from "../icons/move-icon/MoveIcon";
import { PendingIcon } from "../icons/pending-icon/PendingIcon";
import { PermissionsIcon } from "../icons/permissions-icon/PermissionsIcon";
import { PinIcon } from "../icons/pin-icon/PinIcon";
import { PlaylistIcon } from "../icons/playlist-icon/PlaylistIcon";
import { SendIcon } from "../icons/send-icon/SendIcon";
import { ShareIcon } from "../icons/share-icon/ShareIcon";
import { SuccessIcon } from "../icons/success-icon/SuccessIcon";
import { UploadIcon } from "../icons/upload-icon/UploadIcon";

// TEXT

// STYLES
export const MobileFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const MobileFadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

export const DesktopSlideIn = keyframes`
  from { transform: translateX(calc(-100% - 1.5rem)); }
  to { transform: translateX(0); }
`;

export const DesktopSlideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(calc(-100% - 1.5rem)); }
`;

export const Content = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  @media ${minMedium} {
    flex-direction: column;
  }
`;

export const DescriptionContainer = styled.div`
  @media ${maxSmall} {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  }
`;

export const IconBlock = styled.div<{ $alert?: boolean }>`
  display: flex;
  align-items: center;

  svg {
    width: 3rem;
    height: 3rem;
  }

  @media ${minMedium} {
    --icon-size: ${({ $alert }) => ($alert ? "2rem" : "1.5rem")};

    width: 2rem;
    height: 2rem;
    display: flex;
    border-radius: 50%;
    align-items: center;
    justify-content: center;
    background-color: ${shade900};

    svg {
      width: var(--icon-size);
      height: var(--icon-size);
    }
  }
`;

export const DismissIconWrapper = styled.div`
  top: 0.5rem;
  right: 0.5rem;
  position: absolute;

  svg {
    width: 0.83331rem;
    height: 0.83331rem;
  }

  @media ${minMedium} {
    inset: unset;
    position: relative;
    align-self: flex-start;

    ${IconButton} {
      width: 1.5rem;
      height: 1.5rem;
    }

    svg {
      width: 0.625rem;
      height: 0.625rem;
    }
  }
`;

export const ToastBlock = styled.li<ToastBlockProps>`
  gap: 0.75rem;
  width: 16rem;
  display: flex;
  list-style: none;
  min-height: 16rem;
  text-align: center;
  position: relative;
  align-items: center;
  pointer-events: auto;
  flex-direction: column;
  padding: 4.625rem 1.5rem 1.5rem 1.5rem;

  color: ${shade0};
  box-shadow: ${elevation01};
  border: 1px solid ${shade800};
  background-color: ${shade950};
  animation: ${MobileFadeIn} 400ms ease-out 300ms both;

  @media ${minMedium} {
    gap: 1rem;
    width: 32rem;
    padding: 1rem;
    min-height: unset;
    text-align: unset;
    align-items: center;
    flex-direction: row;
    animation: ${DesktopSlideIn} 500ms cubic-bezier(0.16, 1, 0.3, 1) 300ms both;

    &:hover {
      border-color: ${shade700};
    }
  }

  ${({ $isDismissed }) =>
    $isDismissed &&
    css`
      animation: ${MobileFadeOut} 300ms ease-in forwards;

      @media ${minMedium} {
        animation: ${DesktopSlideOut} 300ms cubic-bezier(0.32, 0, 0.67, 0)
          forwards;
      }
    `}
`;

// DATA

// TYPES
export type ToastBlockProps = {
  $isDismissed: boolean;
};

export interface ToastMessageProps extends Pick<ToastContext, "removeToast"> {
  id: string;
  /** Supports `\n` for line breaks, which will be rendered as `<br />` elements. */
  text?: string;
  title?: string;
  dismissible?: boolean;
  autoDismiss?: boolean | number;
  link?: { href?: string; text: string; path?: string };
  variant?:
    | "permissions"
    | "attention"
    | "duplicate"
    | "playlist"
    | "download"
    | "comment"
    | "success"
    | "license"
    | "pending"
    | "upload"
    | "delete"
    | "error"
    | "share"
    | "copy"
    | "info"
    | "like"
    | "move"
    | "send"
    | "pin";
}

// CONSTANTS
export const toastIconMap: Record<
  NonNullable<ToastMessageProps["variant"]>,
  ComponentType
> = {
  permissions: PermissionsIcon,
  attention: AttentionIcon,
  duplicate: DuplicateIcon,
  playlist: PlaylistIcon,
  download: DownloadIcon,
  comment: CommentIcon,
  success: SuccessIcon,
  license: LicenseIcon,
  pending: PendingIcon,
  upload: UploadIcon,
  delete: DeleteIcon,
  error: ErrorIcon,
  share: ShareIcon,
  copy: CopyIcon,
  info: InfoIcon,
  like: LikeIcon,
  move: MoveIcon,
  send: SendIcon,
  pin: PinIcon,
};

export const alertIcons = new Set(["success", "pending", "attention", "error"]);

// FUNCTIONS

// ASSETS
